import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { BodyStyle } from '../typography/styles'
import { Button } from '../button'
import { ThemeSpace } from 'theme/spaces'

export interface SearchInputProps {
  onSearch: (inputValue: string) => void
  placeholder?: string
  isOpen: boolean
  onSetOpen?: (isOpen: boolean) => void
  disabled?: boolean
  searchOnTyping?: boolean
  globalSearch?: boolean
  maxWidth?: ThemeSpace
  query?: string
}

const SearchWrapper = styled.div<{ $isOpen: boolean; $maxWidth: ThemeSpace }>`
  position: relative;

  display: flex;
  align-items: center;

  width: ${({ theme, $isOpen }) => ($isOpen ? '100%' : theme.space.s48)};
  max-width: ${({ theme, $maxWidth }) =>
    $maxWidth ? theme.space[$maxWidth] : theme.size.inputMinWidth};
  transition: width 0.3s ease-in-out;

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: ${({ theme, $isOpen }) => ($isOpen ? '100%' : theme.space.s48)};
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    transition: none;
    max-width: 100%;
  }

  cursor: pointer;
`

const SearchIconButton = styled(Button).attrs({
  icon: 'search',
  size: 'small',
  variant: 'secondary',
})`
  position: absolute;
  left: -${({ theme }) => theme.space.s2};
  button {
    width: ${({ theme }) => theme.space.s48};
    height: 3.3rem;
  }
`

const InputOpenStyle = css`
  padding-right: ${({ theme }) => theme.space.s16};
  cursor: text !important;

  &::placeholder {
    color: ${({ theme }) => theme.color.grey80};
  }

  &::-ms-input-placeholder {
    color: ${({ theme }) => theme.color.grey80};
  }
`

const StyledSearchInput = styled.input<{ value: string; $isOpen: boolean }>`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.color.grey07};
  border: none;
  width: fit-content;
  height: ${({ theme }) => theme.space.s32};
  border-radius: ${({ theme }) => theme.space.s16};
  padding: 0 0 0 ${({ theme }) => theme.space.s48};

  cursor: inherit !important;
  outline: none;

  ${BodyStyle};

  &::placeholder {
    color: transparent;
  }

  &::-ms-input-placeholder {
    color: transparent;
    opacity: 1;
  }

  width: 100%;

  ${({ $isOpen }) => $isOpen && InputOpenStyle};
`

const CloseButton = styled(Button).attrs({
  icon: 'clear',
  variant: 'secondary',
  size: 'small',
})`
  position: absolute;
  right: 0;
`

export const SearchInput = ({
  onSearch,
  placeholder,
  onSetOpen,
  isOpen,
  disabled = false,
  searchOnTyping = false,
  globalSearch = false,
  maxWidth = 's240',
  query = '',
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState<string>('')

  const handleSearch = (value: string) => {
    setValue(value)
    onSearch(value)
  }

  const clearSearch = () => {
    if (globalSearch) {
      setValue('')
    } else {
      handleSearch('')
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch(value)
    }
  }

  useEffect(() => {
    if (isOpen && onSetOpen && globalSearch) {
      inputRef.current?.focus()
    }

    if (!isOpen) {
      setValue('')
    }
  }, [isOpen])

  useEffect(() => {
    if (query) {
      handleSearch(query)
    }
  }, [query])

  return (
    <SearchWrapper $maxWidth={maxWidth} $isOpen={isOpen}>
      <SearchIconButton
        tabIndex={-1}
        onClick={() => onSetOpen && onSetOpen(true)}
      />
      <StyledSearchInput
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        $isOpen={isOpen}
        onChange={({ target }) =>
          searchOnTyping ? handleSearch(target.value) : setValue(target.value)
        }
        onKeyDown={e => handleKeyDown(e)}
        onFocus={() => onSetOpen && onSetOpen(true)}
        onBlur={() => !value && onSetOpen && onSetOpen(false)}
        disabled={disabled}
      />
      {!!value && <CloseButton onClick={clearSearch} />}
    </SearchWrapper>
  )
}
